<div class="container-fluid">
  <!-- Header -->
  <div class="row mb-4">
    <div class="col">
      <h3>Create Workflow Migration</h3>
      <p class="text-muted">
        Create a new workflow migration record to track workflow migrations from
        metadata files.
      </p>
    </div>
  </div>

  <!-- Success Message -->
  <div *ngIf="success" class="row mb-4">
    <div class="col">
      <div class="alert alert-success alert-dismissible fade show" role="alert">
        <i class="fa fa-check-circle"></i>
        <strong>Success!</strong> Workflow migration record created
        successfully. Redirecting...
        <button
          type="button"
          class="close"
          data-dismiss="alert"
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Error Message -->
  <div *ngIf="error" class="row mb-4">
    <div class="col">
      <div class="alert alert-danger alert-dismissible fade show" role="alert">
        <i class="fa fa-exclamation-triangle"></i>
        <strong>Error:</strong> {{ error }}
        <button
          type="button"
          class="close"
          data-dismiss="alert"
          aria-label="Close"
          (click)="error = null"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Form -->
  <div class="row">
    <div class="col-lg-8">
      <div class="card">
        <div class="card-header">
          <h5 class="mb-0">Workflow Migration Details</h5>
        </div>
        <div class="card-body">
          <app-loader *ngIf="loading"></app-loader>

          <form *ngIf="!loading" (ngSubmit)="saveMigrationProcessing()">
            <!-- Jurisdiction Selector (only if no client context) -->
            <div *ngIf="!hasClientContext" class="form-group">
              <label for="jurisdictionSelect" class="form-label">
                <strong>Jurisdiction</strong>
                <span class="text-danger">*</span>
              </label>
              <select
                id="jurisdictionSelect"
                class="form-control"
                [(ngModel)]="migrationProcessing.clientId"
                name="jurisdiction"
                (ngModelChange)="onJurisdictionSelected($event)"
                required
              >
                <option [ngValue]="null">-- Select a Jurisdiction --</option>
                <option
                  *ngFor="let jurisdiction of jurisdictions"
                  [ngValue]="jurisdiction.id"
                >
                  {{ jurisdiction.name }}
                </option>
              </select>
              <small class="form-text text-muted">
                Select the jurisdiction for this workflow migration.
              </small>
            </div>

            <!-- Jurisdiction Display (if client context) -->
            <div *ngIf="hasClientContext" class="form-group">
              <label class="form-label">
                <strong>Jurisdiction</strong>
              </label>
              <p class="form-control-plaintext">
                {{ migrationProcessing.jurisdiction }}
              </p>
            </div>

            <!-- Project Name -->
            <div class="form-group">
              <label for="sourceSystem" class="form-label">
                <strong>Project Name</strong>
                <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                id="sourceSystem"
                class="form-control"
                [(ngModel)]="migrationProcessing.sourceSystem"
                name="sourceSystem"
                placeholder="Enter project or workflow name (e.g., Dog License, Building Permit)"
                required
              />
              <small class="form-text text-muted">
                Enter a descriptive name for the workflow being migrated (this
                will become the workflow name).
              </small>
            </div>

            <!-- Metadata File Upload -->
            <div class="form-group">
              <label class="form-label">
                <strong>Metadata File</strong>
                <span class="text-danger">*</span>
              </label>
              <wm-upload
                #metadataUploadComponent
                buttonText="Select Metadata File"
                validDocumentTypes="json"
                [pathSegments]="['migration-processing', uploadKey]"
                (fileUploaded)="onMetadataFileUploaded($event)"
                (fileDeleted)="onMetadataFileDeleted($event)"
                [key]="uploadKey + '-metadata'"
                [allowMultipleFiles]="false"
                category="MigrationProcessing"
                [deleteStoredDocument]="false"
                [showUploadedFilesList]="true"
              ></wm-upload>
              <small class="form-text text-muted">
                <strong>JSON Metadata File (Blueprint):</strong> Defines field
                types, validations, options, and conditional logic. Uses
                MatchedTemplateCode to link with CSV Template Codes. Organized
                by Activities and Fields.
              </small>
              <div class="mt-2">
                <a
                  href="assets/sample-migration-metadata-comprehensive.json"
                  download="sample-migration-metadata-comprehensive.json"
                  class="btn btn-sm btn-outline-info"
                  target="_blank"
                >
                  <i class="fa fa-download me-2"></i>
                  Download Sample Metadata File
                </a>
                <small class="form-text text-muted ml-2 d-inline">
                  Comprehensive example with all supported features
                </small>
              </div>
              <div class="mt-2">
                <a
                  href="assets/sample-migration-metadata-renewable.json"
                  download="sample-migration-metadata-renewable.json"
                  class="btn btn-sm btn-outline-success"
                  target="_blank"
                >
                  <i class="fa fa-download me-2"></i>
                  Download Renewable Workflow Sample
                </a>
                <small class="form-text text-muted ml-2 d-inline">
                  Example renewable workflow with expiration formula
                </small>
              </div>

              <!-- Metadata Validation Status -->
              <div *ngIf="metadataValidationStatus" class="mt-3">
                <div
                  class="alert"
                  [ngClass]="{
                    'alert-success':
                      !metadataValidationStatus.isValidating &&
                      metadataValidationStatus.isValid,
                    'alert-warning':
                      !metadataValidationStatus.isValidating &&
                      !metadataValidationStatus.isValid &&
                      metadataValidationStatus.warnings.length > 0 &&
                      metadataValidationStatus.errors.length === 0,
                    'alert-danger':
                      !metadataValidationStatus.isValidating &&
                      metadataValidationStatus.errors.length > 0,
                    'alert-info': metadataValidationStatus.isValidating
                  }"
                >
                  <div *ngIf="metadataValidationStatus.isValidating">
                    <i class="fa fa-spinner fa-spin me-2"></i>
                    <strong>Validating metadata...</strong>
                  </div>
                  <div
                    *ngIf="
                      !metadataValidationStatus.isValidating &&
                      metadataValidationStatus.isValid &&
                      metadataValidationStatus.errors.length === 0
                    "
                  >
                    <i class="fa fa-check-circle me-2"></i>
                    <strong>Basic Metadata Validation Passed!</strong>
                    <p class="mb-0 mt-2">
                      All data entity types are supported. Comprehensive
                      validation (field references, activity order, conditional
                      logic) will run when you create the migration.
                    </p>
                  </div>
                  <div
                    *ngIf="
                      !metadataValidationStatus.isValidating &&
                      metadataValidationStatus.errors.length > 0
                    "
                  >
                    <i class="fa fa-exclamation-triangle me-2"></i>
                    <strong>Metadata Validation Failed</strong>
                    <ul class="mb-0 mt-2">
                      <li *ngFor="let error of metadataValidationStatus.errors">
                        {{ error }}
                      </li>
                    </ul>
                  </div>
                  <div
                    *ngIf="
                      !metadataValidationStatus.isValidating &&
                      metadataValidationStatus.warnings.length > 0 &&
                      metadataValidationStatus.errors.length === 0
                    "
                  >
                    <i class="fa fa-exclamation-circle me-2"></i>
                    <strong>Metadata Validation Warnings</strong>
                    <ul class="mb-0 mt-2">
                      <li
                        *ngFor="
                          let warning of metadataValidationStatus.warnings
                        "
                      >
                        {{ warning }}
                      </li>
                    </ul>
                  </div>
                </div>

                <!-- Metadata Validation Details -->
                <div
                  *ngIf="!metadataValidationStatus.isValidating"
                  class="mt-2"
                >
                  <button
                    class="btn btn-sm btn-outline-info"
                    type="button"
                    (click)="
                      showMetadataValidationDetails = !showMetadataValidationDetails
                    "
                  >
                    <i
                      class="fa"
                      [ngClass]="
                        showMetadataValidationDetails
                          ? 'fa-chevron-up'
                          : 'fa-chevron-down'
                      "
                    ></i>
                    {{ showMetadataValidationDetails ? 'Hide' : 'Show' }}
                    Validation Details
                  </button>

                  <div *ngIf="showMetadataValidationDetails" class="mt-3">
                    <h6>
                      Supported Data Entity Types ({{
                        metadataValidationStatus.supportedTypes.length
                      }})
                    </h6>
                    <p class="text-muted small mb-2">
                      Data entity types that are supported by the migration
                      method.
                    </p>
                    <div class="d-flex flex-wrap gap-2 mb-3">
                      <span
                        *ngFor="
                          let type of metadataValidationStatus.supportedTypes
                        "
                        class="badge bg-success text-white"
                      >
                        {{ type }}
                      </span>
                    </div>
                    <p
                      *ngIf="
                        metadataValidationStatus.supportedTypes.length === 0
                      "
                      class="text-muted small"
                    >
                      No supported data entity types found.
                    </p>

                    <h6>
                      Unsupported Data Entity Types ({{
                        metadataValidationStatus.unsupportedTypes.length
                      }})
                    </h6>
                    <p class="text-muted small mb-2">
                      Data entity types found in the metadata that are not
                      supported by the migration method.
                    </p>
                    <div class="d-flex flex-wrap gap-2 mb-3">
                      <span
                        *ngFor="
                          let type of metadataValidationStatus.unsupportedTypes
                        "
                        class="badge bg-danger text-white"
                      >
                        {{ type }}
                      </span>
                    </div>
                    <p
                      *ngIf="
                        metadataValidationStatus.unsupportedTypes.length === 0
                      "
                      class="text-muted small"
                    >
                      All data entity types in the metadata are supported.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Application Data File Upload -->
            <div class="form-group">
              <label class="form-label">
                <strong>Application Data File</strong>
              </label>
              <wm-upload
                #applicationDataUploadComponent
                buttonText="Select Application Data File"
                validDocumentTypes="json"
                [pathSegments]="['migration-processing', uploadKey]"
                (fileUploaded)="onApplicationDataFileUploaded($event)"
                (fileDeleted)="onApplicationDataFileDeleted($event)"
                [key]="uploadKey + '-appdata'"
                [allowMultipleFiles]="false"
                category="MigrationProcessing"
                [deleteStoredDocument]="false"
                [showUploadedFilesList]="true"
              ></wm-upload>
              <small class="form-text text-muted">
                <strong>Application Data File (Content - Optional):</strong>
                Contains actual application data to import. Organized by
                Application ID → Activity Name → Field Values. Use after
                workflow is created.
              </small>
              <div class="mt-2">
                <a
                  href="assets/sample-application-data-comprehensive.json"
                  download="sample-application-data-comprehensive.json"
                  class="btn btn-sm btn-outline-info"
                  target="_blank"
                >
                  <i class="fa fa-download me-2"></i>
                  Download Sample Application Data File
                </a>
                <small class="form-text text-muted ml-2 d-inline">
                  Example application data with multiple applications
                </small>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="form-group mt-4">
              <button
                type="submit"
                class="btn btn-primary mr-2"
                [disabled]="
                  loading ||
                  !migrationProcessing.clientId ||
                  !migrationProcessing.sourceSystem ||
                  !metadataUploaded ||
                  (metadataValidationStatus &&
                    metadataValidationStatus.errors.length > 0) ||
                  (metadataValidationStatus &&
                    metadataValidationStatus.isValidating)
                "
              >
                <i class="fa fa-save"></i>
                Create Workflow Migration
              </button>
              <button
                type="button"
                class="btn btn-secondary"
                (click)="cancel()"
                [disabled]="loading"
              >
                <i class="fa fa-times"></i>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Help Panel -->
    <div class="col-lg-4">
      <!-- Overview Card -->
      <div class="card mb-3">
        <div class="card-header">
          <h5 class="mb-0">
            <i class="fa fa-info-circle"></i>
            Migration Overview
          </h5>
        </div>
        <div class="card-body">
          <h6>What is Workflow Migration?</h6>
          <p>
            Workflow Migration helps you transfer your permit workflows from a
            legacy system into GeoPermits. You'll provide three coordinated
            files that work together to recreate your workflow structure and
            data.
          </p>

          <h6>What You'll Need</h6>
          <ul class="mb-0">
            <li>
              <strong>Jurisdiction:</strong> Which jurisdiction/client this
              workflow belongs to
            </li>
            <li>
              <strong>Project Name:</strong> A descriptive name for the workflow
              (e.g., "Dog License", "Building Permit")
            </li>
            <li>
              <strong>Metadata File:</strong> JSON file defining activities,
              fields, data entity types, and properties
            </li>
            <li>
              <strong>Application Data:</strong> (Optional) Your existing
              applications to migrate
            </li>
          </ul>
        </div>
      </div>

      <!-- File Relationships Card -->
      <div class="card mb-3">
        <div class="card-header">
          <h5 class="mb-0">
            <i class="fa fa-link"></i>
            How the Files Work Together
          </h5>
        </div>
        <div class="card-body">
          <div>
            <div class="mb-3">
              <div class="d-flex align-items-center mb-2">
                <span class="badge bg-success text-white mr-2">1</span>
                <strong>JSON Metadata File</strong>
              </div>
              <p class="mb-2 ml-4">
                The <em>workflow blueprint</em> that defines:
              </p>
              <ul class="mb-2 ml-4">
                <li>Data entity types (text, dropdown, date, etc.)</li>
                <li>Validation rules and requirements</li>
                <li>Options for list-based data entities</li>
                <li>Conditional visibility and logic</li>
                <li>Activity structure and organization</li>
              </ul>
              <p class="mb-0 ml-4 text-muted">
                <small
                  ><strong>Format:</strong> JSON file with Activities and Fields
                  structure</small
                >
              </p>
            </div>

            <div class="mb-2">
              <div class="d-flex align-items-center mb-2">
                <span class="badge bg-info text-white mr-2">2</span>
                <strong>Application Data File</strong>
              </div>
              <p class="mb-2 ml-4">
                The <em>application repository</em> that contains:
              </p>
              <ul class="mb-2 ml-4">
                <li>Existing applications from your legacy system</li>
                <li>Data organized by application ID</li>
                <li>Activity-based data grouping</li>
              </ul>
              <p class="mb-0 ml-4 text-muted">
                <small
                  ><strong>Future Feature:</strong> Application data import is
                  coming soon</small
                >
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Migration Process Card -->
      <div class="card mb-3">
        <div class="card-header">
          <h5 class="mb-0">
            <i class="fa fa-cogs"></i>
            Migration Process
          </h5>
        </div>
        <div class="card-body">
          <div>
            <p class="mb-2">The migration workflow:</p>
            <ol class="mb-0 pl-3">
              <li class="mb-2">
                <strong>Upload & Parse Metadata</strong>
                <p class="mb-0 text-muted">
                  System automatically parses JSON metadata to identify
                  activities and fields, creating editable Migration Processing
                  Data Entities
                </p>
              </li>
              <li class="mb-2">
                <strong>Review & Edit Data Entities</strong>
                <p class="mb-0 text-muted">
                  Review generated data entities, adjust labels, template codes,
                  and target activities. Reorder fields and organize by sections
                </p>
              </li>
              <li class="mb-2">
                <strong>Execute Migration</strong>
                <p class="mb-0 text-muted">
                  System builds workflow activities using your edited data
                  entity mappings, creating data entities with proper types and
                  validations. Routes from metadata are processed to create
                  workflow navigation and conditional routing with automatic
                  Decision Activity creation
                </p>
              </li>
              <li class="mb-2">
                <strong>Verify Workflow</strong>
                <p class="mb-0 text-muted">
                  Test the created workflow, verify activities and data entities
                  are configured correctly
                </p>
              </li>
              <li class="mb-0">
                <strong>Migrate Application Data</strong>
                <p class="mb-0 text-muted">
                  (Coming Soon) Import existing application data into the new
                  workflow structure
                </p>
              </li>
            </ol>
          </div>
        </div>
      </div>

      <!-- Data Entity Editing Example Card -->
      <div class="card mb-3">
        <div class="card-header">
          <h5 class="mb-0">
            <i class="fa fa-lightbulb"></i>
            Example: Customizing Data Entities
          </h5>
        </div>
        <div class="card-body">
          <div>
            <p class="mb-2">
              <strong>How "Dog Breed" gets migrated:</strong>
            </p>

            <div
              class="bg-light p-3 mb-2"
              style="border-left: 3px solid #007bff;"
            >
              <strong>1. Metadata Parsed:</strong><br />
              <span
                >Field "<span class="text-primary">BreedID</span>" in activity
                "<span class="text-success">DOG DETAILS</span>"</span
              ><br />
              <span
                >Creates data entity with Label: "BreedID", Template Code:
                "Breed"</span
              >
            </div>

            <div class="text-center mb-2">
              <i class="fa fa-arrow-down text-muted"></i>
            </div>

            <div
              class="bg-light p-3 mb-2"
              style="border-left: 3px solid #ffc107;"
            >
              <strong>2. You Edit:</strong><br />
              <span
                >Change Label to "<span class="text-primary">Dog Breed</span
                >"</span
              ><br />
              <span>Assign to Target Activity: "Dog Information"</span>
            </div>

            <div class="text-center mb-2">
              <i class="fa fa-arrow-down text-muted"></i>
            </div>

            <div class="bg-light p-3" style="border-left: 3px solid #28a745;">
              <strong>3. Migration Creates:</strong><br />
              <span
                >Dropdown data entity labeled "<span class="text-primary"
                  >Dog Breed</span
                >" in "<span class="text-success">Dog Information</span>"
                activity with breed options from metadata</span
              >
            </div>
          </div>
        </div>
      </div>

      <!-- File Format Tips Card -->
      <div class="card mb-3">
        <div class="card-header">
          <h5 class="mb-0">
            <i class="fa fa-check-circle"></i>
            File Requirements
          </h5>
        </div>
        <div class="card-body">
          <div>
            <h6 class="mb-2">Metadata JSON Structure:</h6>
            <ul class="mb-3">
              <li>Contains an Activities array with all workflow activities</li>
              <li>
                Each activity contains a Fields array for its data entities
              </li>
              <li>MatchedTemplateCode property identifies data entities</li>
              <li>
                Type property determines the data entity control type (for
                fields) or activity type (for activities)
              </li>
              <li>
                Routes property defines workflow routing and conditional logic
              </li>
              <li>
                Conditions in routes support complex business logic with field
                comparisons
              </li>
            </ul>

            <h6 class="mb-2">Supported Data Entity Types:</h6>
            <div class="d-flex flex-wrap gap-1 mb-2">
              <span class="badge badge-secondary" style="font-size: 0.85rem;"
                >text</span
              >
              <span class="badge badge-secondary" style="font-size: 0.85rem;"
                >textarea</span
              >
              <span class="badge badge-secondary" style="font-size: 0.85rem;"
                >number</span
              >
              <span class="badge badge-secondary" style="font-size: 0.85rem;"
                >decimal</span
              >
              <span class="badge badge-secondary" style="font-size: 0.85rem;"
                >currency</span
              >
              <span class="badge badge-secondary" style="font-size: 0.85rem;"
                >date</span
              >
              <span class="badge badge-secondary" style="font-size: 0.85rem;"
                >datetime</span
              >
              <span class="badge badge-secondary" style="font-size: 0.85rem;"
                >time</span
              >
              <span class="badge badge-secondary" style="font-size: 0.85rem;"
                >dropdown</span
              >
              <span class="badge badge-secondary" style="font-size: 0.85rem;"
                >checkbox</span
              >
              <span class="badge badge-secondary" style="font-size: 0.85rem;"
                >radio</span
              >
              <span class="badge badge-secondary" style="font-size: 0.85rem;"
                >email</span
              >
              <span class="badge badge-secondary" style="font-size: 0.85rem;"
                >phone</span
              >
              <span class="badge badge-secondary" style="font-size: 0.85rem;"
                >file</span
              >
              <span class="badge badge-secondary" style="font-size: 0.85rem;"
                >image</span
              >
              <span class="badge badge-secondary" style="font-size: 0.85rem;"
                >group</span
              >
              <span class="badge badge-secondary" style="font-size: 0.85rem;"
                >table</span
              >
              <span class="badge badge-secondary" style="font-size: 0.85rem;"
                >row</span
              >
              <span class="badge badge-secondary" style="font-size: 0.85rem;"
                >formattedtext</span
              >
              <span class="badge badge-secondary" style="font-size: 0.85rem;"
                >calculated</span
              >
            </div>
            <p class="text-muted mb-0">
              These data entity types are validated during metadata upload
            </p>
          </div>
        </div>
      </div>

      <!-- Supported Activity Types Card -->
      <div class="card mb-3">
        <div class="card-header">
          <h5 class="mb-0">
            <i class="fa fa-tasks"></i>
            Supported Activity Types
          </h5>
        </div>
        <div class="card-body">
          <div>
            <p class="mb-2">
              Activities can be specified in metadata using the "Type" property.
              Supported types:
            </p>
            <div class="row">
              <div class="col-6">
                <ul class="mb-2">
                  <li>
                    <strong>form</strong> - Standard data entry form (default)
                  </li>
                  <li><strong>payment</strong> - Payment processing</li>
                  <li><strong>printtemplate</strong> - Document generation</li>
                  <li><strong>print</strong> - Alias for printtemplate</li>
                  <li><strong>signoff</strong> - Approval/signature step</li>
                  <li>
                    <strong>callworkflow</strong> - Invoke another workflow
                  </li>
                  <li><strong>email</strong> - Send email notifications</li>
                </ul>
              </div>
              <div class="col-6">
                <ul class="mb-0">
                  <li><strong>parcelsearch</strong> - Parcel lookup</li>
                  <li><strong>review</strong> - Review activity</li>
                  <li>
                    <strong>inspection</strong> or
                    <strong>consolidated-inspection</strong> - Consolidated
                    inspection
                  </li>
                  <li>
                    <strong>request-inspection</strong> - Request inspection
                  </li>
                  <li>
                    <strong>schedule-inspection</strong> - Schedule inspection
                  </li>
                  <li>
                    <strong>complete-inspection</strong> - Complete inspection
                  </li>
                  <li>
                    <strong>simple-inspection</strong> - Simple inspection
                  </li>
                  <li>
                    <strong>end</strong> or <strong>complete</strong> - End
                    workflow
                  </li>
                </ul>
              </div>
            </div>
            <p class="text-muted mb-0">
              If no type is specified, defaults to "form". Decision activities
              are created automatically by the routing engine when conditions
              are specified.
            </p>
          </div>
        </div>
      </div>

      <!-- Activity-Specific Properties Card -->
      <div class="card mb-3">
        <div class="card-header">
          <h5 class="mb-0">
            <i class="fa fa-cog"></i>
            Activity-Specific Properties
          </h5>
        </div>
        <div class="card-body">
          <div>
            <p class="mb-2">
              Some activity types support additional properties in metadata:
            </p>

            <h6 class="mt-3">Email Activity</h6>
            <ul class="small mb-2">
              <li>
                <code>RoleRecipients</code> - Array of role names to receive
                email
              </li>
              <li><code>To</code> - Array of static email addresses</li>
              <li><code>CC</code> - Array of CC email addresses</li>
              <li><code>BCC</code> - Array of BCC email addresses</li>
              <li>
                <code>Subject</code> - Email subject line (supports template
                codes)
              </li>
              <li>
                <code>Body</code> - Email body HTML content (supports template
                codes)
              </li>
              <li>
                <code>SendEmailToApplicant</code> - Boolean, when true sends
                email to the applicant's email address
              </li>
            </ul>

            <h6 class="mt-3">PrintTemplate Activity</h6>
            <ul class="small mb-2">
              <li>
                <code>AutoContinue</code> - Boolean, auto-continue to next
                activity after print
              </li>
            </ul>

            <h6 class="mt-3">End/Complete Activity</h6>
            <ul class="small mb-2">
              <li>
                <code>Reason</code> - Completion message displayed to user
              </li>
              <li>
                <code>AutoSubmit</code> - Boolean, auto-submit without showing
                screen
              </li>
              <li>
                <code>RedirectToParent</code> - Boolean, redirect to parent
                workflow
              </li>
            </ul>

            <h6 class="mt-3">ConsolidatedInspection Activity</h6>
            <ul class="small mb-2">
              <li>
                <code>InspectionType</code> - Type of inspection (e.g.,
                "Foundation", "Final")
              </li>
              <li>
                <code>ScheduleStepResponsibleRole</code> - Role name for
                scheduling
              </li>
              <li>
                <code>CompleteStepResponsibleRole</code> - Role name for
                completion
              </li>
            </ul>

            <h6 class="mt-3">Document/File Field Properties</h6>
            <p class="small mb-1">For fields with Type "file" or "image":</p>
            <ul class="small mb-2">
              <li>
                <code>FileTypes</code> - Comma-separated list of allowed file
                extensions (e.g., "pdf,jpg,png" or "pdf,dwg,dxf")
              </li>
              <li>
                <code>RequireReview</code> - Boolean, when true uploaded
                documents will be marked as requiring review
              </li>
            </ul>

            <h6 class="mt-3">Formatted Text Field Properties</h6>
            <p class="small mb-1">For fields with Type "formattedtext":</p>
            <ul class="small mb-2">
              <li>
                <code>HTMLContent</code> - HTML content for the formatted text
                field (use this instead of Label for HTML)
              </li>
              <li>
                <code>DocumentUploads</code> - Array of documents to upload and
                reference in the formatted text. Each document has:
                <ul class="small">
                  <li>
                    <code>Name</code> - Name used to reference the document (use
                    <code>$&#123;Name&#125;</code> in HTMLContent to insert
                    link)
                  </li>
                  <li>
                    <code>FilePath</code> - Path to the document file to upload.
                    Supports:
                    <ul class="small">
                      <li>
                        HTTP/HTTPS URLs (e.g.,
                        "https://example.com/document.pdf")
                      </li>
                      <li>
                        UNC paths (e.g., "\\\\server\\share\\document.pdf")
                      </li>
                      <li>
                        Local file paths (e.g., "C:\\Documents\\document.pdf")
                      </li>
                    </ul>
                  </li>
                  <li>
                    <code>LinkText</code> - Text to display for the link
                    (optional, defaults to filename)
                  </li>
                </ul>
              </li>
              <li>
                Example: If <code>HTMLContent</code> contains "Click
                <code>$&#123;PolicyDoc&#125;</code> to view", and
                DocumentUploads has a document with
                <code>Name: "PolicyDoc"</code>, the placeholder will be replaced
                with a clickable link to the uploaded document
              </li>
            </ul>

            <h6 class="mt-3">Calculated Field Properties</h6>
            <p class="small mb-1">For fields with Type "calculated":</p>
            <ul class="small mb-0">
              <li>
                <code>FormulaOutputs</code> - Array of formula outputs evaluated
                in order. Each output has:
                <ul class="small">
                  <li>
                    <code>Value</code> - The value to return if this output's
                    conditions are met
                  </li>
                  <li>
                    <code>Conditions</code> - Array of conditions to evaluate.
                    Each condition has:
                    <ul class="small">
                      <li>
                        <code>Field</code> - Template code of the field to
                        evaluate
                      </li>
                      <li>
                        <code>Operator</code> - Comparison operator (IS,
                        GREATER_THAN, LESS_THAN, CONTAINS, etc.)
                      </li>
                      <li><code>Value</code> - The value to compare against</li>
                    </ul>
                  </li>
                  <li>
                    <code>LogicalOperator</code> - How to combine conditions:
                    "AND" or "OR" (default: "AND")
                  </li>
                  <li>
                    <code>IsOtherwise</code> - If true, this output is used as
                    the default when no other conditions match (optional)
                  </li>
                </ul>
              </li>
              <li>
                Formula evaluation: FormulaOutputs are evaluated in order until
                conditions are met, then the corresponding Value is returned
              </li>
            </ul>

            <h6 class="mt-3">Fee Field Properties</h6>
            <p class="small mb-1">
              For fields with Type "fee" (inherits from calculated, also uses
              FormulaOutputs):
            </p>
            <ul class="small mb-0">
              <li>
                <code>FeeType</code> - Type of fee calculation: "Simple",
                "List", or "Advanced" (default: "Simple")
                <ul class="small">
                  <li>
                    <strong>Simple:</strong> Flat fee with single FormulaOutput
                    (no conditions). Example: Fixed $100 fee.
                  </li>
                  <li>
                    <strong>Advanced:</strong> Multiple FormulaOutputs with
                    conditions for tiered/conditional pricing. Example: $750 if
                    ProjectValue > $200k, $500 if > $100k, etc.
                  </li>
                  <li>
                    <strong>List:</strong> FormulaOutputs based on list field
                    values. Requires ListTemplateCode. Example: Different fee
                    for each ProjectType (new, renovation, etc.).
                  </li>
                </ul>
              </li>
              <li>
                <code>FeeMinimum</code> - Minimum fee amount (optional,
                typically used with Advanced type)
              </li>
              <li>
                <code>FeeMaximum</code> - Maximum fee amount (optional,
                typically used with Advanced type)
              </li>
              <li>
                <code>FeeRecipient</code> - Fee recipient name/identifier
                (optional)
              </li>
              <li>
                <code>ListTemplateCode</code> - Template code of list field
                (dropdown/radio/checkbox) to reference (required when FeeType is
                "List")
              </li>
              <li>
                <code>FormulaOutputs</code> - Array of fee outputs:
                <ul class="small">
                  <li>Simple: One output with just Value, no Conditions</li>
                  <li>
                    Advanced: Multiple outputs with Conditions for each tier
                  </li>
                  <li>
                    List: One output per list item value with Condition matching
                    that value
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Routing Configuration Card -->
      <div class="card mb-3">
        <div class="card-header">
          <h5 class="mb-0">
            <i class="fa fa-code-branch"></i>
            Workflow Routing
          </h5>
        </div>
        <div class="card-body">
          <div>
            <h6>Routing Features</h6>
            <p class="mb-2">
              Define workflow routing in metadata using the "Routes" property on
              activities:
            </p>
            <ul class="mb-2">
              <li>Specify target activities for workflow progression</li>
              <li>Add conditional routing based on field values</li>
              <li>Automatic Decision Activity creation for complex routing</li>
              <li>Support for multiple routes with different conditions</li>
              <li>
                Customize decision activity names with
                <code>RouteActivity</code> property
              </li>
            </ul>

            <h6>Route Properties</h6>
            <ul class="small mb-2">
              <li><code>Name</code> - Descriptive name for the route</li>
              <li><code>ToActivity</code> - Target activity name (required)</li>
              <li><code>Conditions</code> - Array of conditions to evaluate</li>
              <li>
                <code>LogicalOperator</code> - "AND" or "OR" for combining
                conditions
              </li>
              <li>
                <code>RouteActivity</code> - Optional custom screen name for the
                decision activity created when routes have conditions. If not
                specified, defaults to "Route from [ActivityName]"
              </li>
            </ul>

            <h6>Supported Route Operators</h6>
            <div class="d-flex flex-wrap gap-1 mb-2">
              <span class="badge badge-info" style="font-size: 0.85rem;"
                >IS / == / EQUALS</span
              >
              <span class="badge badge-info" style="font-size: 0.85rem;"
                >IS_NOT / != / NOT_EQUALS</span
              >
              <span class="badge badge-info" style="font-size: 0.85rem;"
                >GREATER_THAN / > / GT</span
              >
              <span class="badge badge-info" style="font-size: 0.85rem;"
                >LESS_THAN / < / LT</span
              >
              <span class="badge badge-info" style="font-size: 0.85rem;"
                >EXISTS / !!</span
              >
              <span class="badge badge-info" style="font-size: 0.85rem;"
                >NOT_EXISTS / !</span
              >
              <span class="badge badge-info" style="font-size: 0.85rem;"
                >CONTAINS / ~</span
              >
              <span class="badge badge-info" style="font-size: 0.85rem;"
                >DOES_NOT_CONTAIN / !~</span
              >
              <span class="badge badge-info" style="font-size: 0.85rem;"
                >RANGE / ~~</span
              >
            </div>

            <h6>Logical Operators</h6>
            <p class="mb-2">
              Combine multiple conditions using:
            </p>
            <ul class="mb-2">
              <li>
                <strong>AND</strong> - All conditions must be true (default)
              </li>
              <li><strong>OR</strong> - Any condition must be true</li>
            </ul>

            <h6>Example Route Configuration</h6>
            <pre
              class="bg-light p-3 mb-0"
              style="font-size: 0.9em;"
              ngNonBindable
            >
&#123;
  "ActivityName": "Application Form",
  "Routes": [
    &#123;
      "Name": "Needs Review",
      "ToActivity": "Manager Review",
      "RouteActivity": "Application Review Decision",
      "Conditions": [
        &#123;
          "Field": "ApplicationValue",
          "Operator": "GREATER_THAN",
          "Value": "10000"
        &#125;
      ]
    &#125;,
    &#123;
      "Name": "Auto Approve",
      "ToActivity": "Approval",
      "Conditions": []
    &#125;
  ]
&#125;</pre
            >
          </div>
        </div>
      </div>

      <!-- Next Steps Card -->
      <div class="card">
        <div class="card-header">
          <h5 class="mb-0">
            <i class="fa fa-forward"></i>
            Next Steps
          </h5>
        </div>
        <div class="card-body">
          <p class="mb-2">
            After creating this migration record:
          </p>
          <ol class="mb-0 pl-3">
            <li>
              System automatically parses metadata and creates data entities
            </li>
            <li>You'll be redirected to the migration processing list</li>
            <li>
              Open your migration to review Migration Processing Data Entities
            </li>
            <li>Edit labels, template codes, and target activities inline</li>
            <li>Reorder entities and organize by section as needed</li>
            <li>
              Workflow routing is configured from metadata Routes property
            </li>
            <li>
              Execute the migration to build the GeoPermits workflow with
              routing
            </li>
            <li>Verify the workflow, routing logic, and mark as complete</li>
          </ol>
        </div>
      </div>
    </div>
  </div>
</div>
